import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
//
import Iconify from '../iconify';
//
import RejectionFiles from './errors-rejection-files';

// ----------------------------------------------------------------------

export default function ProjectUpload({
  disabled,
  multiple = false,
  error,
  helperText,
  //
  file,
  onDelete,
  uploadProgress = 0,
  uploadStatus = 'idle',
  //
  files,
  thumbnail,
  onUpload,
  onRetry,
  onRemove,
  onRemoveAll,
  maxSizeString,
  sx,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    accept: {
      'application/pdf': ['.pdf'],
    },
    onDrop: (acceptedFiles) => {
      // set file state here
      onUpload(acceptedFiles[0]);
    },
  });
  const getProgressColor = () => {
    if (uploadStatus === 'error') return 'error';
    if (uploadStatus === 'success') return 'success';
    return 'warning'; // yellow while uploading
  };

  const getTrashColor = () => {
    if (uploadStatus === 'error') return 'error.main';
    if (uploadStatus === 'success') return 'success.main';
    return 'warning.main';
  };

  const hasFile = !!file && !multiple;

  const hasError = isDragReject || !!error;

  const renderPlaceholder = (
    <Stack spacing={3} alignItems="center" justifyContent="center" flexWrap="wrap">
      {/* <UploadIllustration sx={{ width: 1, maxWidth: 200 }} /> */}
      <img src="/assets/illustrations/upload-illustration.svg" alt="upload" />
      <Stack spacing={1} sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <Box
            component="span"
            sx={{
              mx: 0.5,
              color: 'primary.main',
            }}
          >
            Click here
          </Box>
          to upload your file or drag and drop.
        </Typography>
        <Typography variant="body2">Supported Format: PDF </Typography>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      <Box
        {...getRootProps()}
        sx={{
          py: 2,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',

          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': {
            opacity: 0.72,
          },

          ...(hasError && {
            color: 'error.main',
            borderColor: 'error.main',
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
          }),
        }}
      >
        <input {...getInputProps()} />

        {renderPlaceholder}
      </Box>

      {hasFile && (
        <Stack gap={2} alignItems="stretch" justifyContent="center">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            spacing={2}
            sx={{
              mt: 3,
              p: 2,
              boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
              borderRadius: 1,

              bgcolor: (theme) => alpha('#FFFFFF', 0.08),
              border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
              transition: (theme) => theme.transitions.create(['opacity', 'padding']),
            }}
          >
            {['uploading', 'idle'].includes(uploadStatus) && (
              <img src="/assets/illustrations/file-progress.svg" alt="pdf" />
            )}
            {uploadStatus === 'success' && (
              <img src="/assets/illustrations/file-complete.svg" alt="pdf" />
            )}
            {uploadStatus === 'error' && (
              <img src="/assets/illustrations/file-error.svg" alt="pdf" />
            )}
            <Stack flexGrow={1} gap={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography fontSize={16} fontWeight={600} color="text.primary">
                  {file.name}
                </Typography>
                <Iconify
                  icon="uil:trash"
                  color={getTrashColor()}
                  width={20}
                  height={20}
                  onClick={onDelete}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>

              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                color={getProgressColor()}
                sx={{ height: 8 }}
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography fontSize={14} color="text.secondary">
                  {uploadStatus === 'uploading' && 'Uploading...'}
                  {uploadStatus === 'success' && 'Upload Successful!'}
                  {uploadStatus === 'error' && 'Upload Failed'}
                </Typography>

                {uploadStatus !== 'error' ? (
                  <Typography fontSize={14} color="text.secondary">
                    {uploadProgress}%
                  </Typography>
                ) : (
                  <Stack
                    onClick={onRetry}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Typography fontSize={12} color="error.main" fontWeight={600}>
                      Try again
                    </Typography>
                    <img src="/assets/icons/upload/retry.svg" alt="retry" width={13} height={13} />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}

      <RejectionFiles fileRejections={fileRejections} maxSizeString={maxSizeString} />
    </Box>
  );
}

ProjectUpload.propTypes = {
  disabled: PropTypes.object,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  files: PropTypes.array,
  helperText: PropTypes.object,
  multiple: PropTypes.bool,
  onDelete: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  onUpload: PropTypes.func,
  onRetry: PropTypes.func,
  sx: PropTypes.object,
  thumbnail: PropTypes.bool,
  maxSizeString: PropTypes?.string,
  uploadProgress: PropTypes.number,
  uploadStatus: PropTypes.string,
};
